import { supabase } from "../../Services/supabaseClient";
import { ensureCrmPipeline } from "./crmBootstrapService";

const normalizeScore = (lead) => {
  let score = 0;

  if (lead.email) score += 20;
  if (lead.phone) score += 20;
  if (lead.business_name) score += 20;
  if (lead.estimated_value && Number(lead.estimated_value) > 0) score += 20;
  if (lead.rating === "hot") score += 20;
  if (lead.rating === "warm") score += 10;

  return score;
};

export const getLeads = async (companyId) => {
  const { data, error } = await supabase
    .from("crm_leads")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

export const createLead = async (payload) => {
  const enrichedPayload = {
    ...payload,
    score: normalizeScore(payload),
  };

  const { data, error } = await supabase
    .from("crm_leads")
    .insert([enrichedPayload])
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

export const updateLead = async (leadId, companyId, payload) => {
  const enrichedPayload = {
    ...payload,
    score: normalizeScore(payload),
  };

  const { data, error } = await supabase
    .from("crm_leads")
    .update(enrichedPayload)
    .eq("id", leadId)
    .eq("company_id", companyId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

export const deleteLead = async (leadId, companyId) => {
  const { error } = await supabase
    .from("crm_leads")
    .delete()
    .eq("id", leadId)
    .eq("company_id", companyId);

  if (error) {
    throw error;
  }

  return true;
};

export const convertLead = async (leadId, companyId, ownerId) => {
  const { data: lead, error: leadError } = await supabase
    .from("crm_leads")
    .select("*")
    .eq("id", leadId)
    .eq("company_id", companyId)
    .single();

  if (leadError) {
    throw leadError;
  }

  const { data: account, error: accountError } = await supabase
    .from("crm_accounts")
    .insert([
      {
        company_id: companyId,
        owner_id: ownerId,
        name: lead.business_name || lead.lead_name || "Unnamed Account",
        email: lead.email || null,
        phone: lead.phone || null,
        industry: lead.industry || null,
        account_type: "prospect",
        status: "active",
      },
    ])
    .select()
    .single();

  if (accountError) {
    throw accountError;
  }

  const { data: contact, error: contactError } = await supabase
    .from("crm_contacts")
    .insert([
      {
        company_id: companyId,
        account_id: account.id,
        owner_id: ownerId,
        full_name: lead.lead_name || lead.business_name || "Unnamed Contact",
        email: lead.email || null,
        phone: lead.phone || null,
        is_primary: true,
      },
    ])
    .select()
    .single();

  if (contactError) {
    throw contactError;
  }

  const { error: pipelineError } = await supabase
    .from("crm_pipelines")
    .select("id, is_default, created_at")
    .eq("company_id", companyId)
    .order("is_default", { ascending: false })
    .order("created_at", { ascending: true })
    .limit(1);

  if (pipelineError) {
    throw pipelineError;
  }

  const pipeline = await ensureCrmPipeline(companyId);

  if (!pipeline?.id) {
    throw new Error("No CRM pipeline found for this company");
  }

  const { data: stages, error: stageError } = await supabase
    .from("crm_pipeline_stages")
    .select("id, default_probability")
    .eq("pipeline_id", pipeline.id)
    .order("position", { ascending: true })
    .limit(1);

  if (stageError) {
    throw stageError;
  }

  const stage = stages?.[0];

  if (!stage?.id) {
    throw new Error("No CRM pipeline stage found for this company");
  }

  const { data: opportunity, error: opportunityError } = await supabase
    .from("crm_opportunities")
    .insert([
      {
        company_id: companyId,
        owner_id: ownerId,
        account_id: account.id,
        primary_contact_id: contact.id,
        lead_id: lead.id,
        name: `${lead.business_name || lead.lead_name || "New"} Deal`,
        amount: Number(lead.estimated_value || 0),
        pipeline_id: pipeline.id,
        stage_id: stage.id,
        probability: Number(stage.default_probability || 0),
        status: "open",
        lead_source: lead.lead_source || null,
      },
    ])
    .select()
    .single();

  if (opportunityError) {
    throw opportunityError;
  }

  const { error: updateError } = await supabase
    .from("crm_leads")
    .update({
      status: "converted",
      converted_account_id: account.id,
      converted_contact_id: contact.id,
      converted_opportunity_id: opportunity.id,
      last_contact_at: new Date().toISOString(),
    })
    .eq("id", leadId)
    .eq("company_id", companyId);

  if (updateError) {
    throw updateError;
  }

  return {
    account,
    contact,
    opportunity,
  };
};
